import { Button, Rate, Tag, Tooltip } from "antd";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import dayjs from "dayjs";
import {
  CopyOutlined,
  EditOutlined,
  FilePdfOutlined,
  FileTextOutlined,
  FileWordOutlined,
  MinusCircleOutlined,
  PlusCircleOutlined,
  StarFilled,
  StopOutlined,
} from "@ant-design/icons";
import isEmpty from "../../validations/is-empty";

const STATUS_LIST = [
  "Recruiter Phone",
  "Recruiter Video",
  "HR Call",
  "Technical",
  "Behavioural",
  "Final",
  "Failed",
];

const CallHorizonView = ({
  searchedData,
  handleClickEdit,
  handleDownloadResume,
  handleDownloadPDF,
  handleDownloadJD,
  handleCopyDetail,
  handleReviewModalOpen,
  allStatus,
  rateValues,
  canUseSpecialActions,
}) => {
  const themeMode = useSelector((state) => state.themeMode);
  const timezoneMode = useSelector((state) => state.timezoneMode);
  const [groupedData, setGroupedData] = useState({ columns: {} });
  const [expandedIds, setExpandedIds] = useState([]);
  const [currentTime, setCurrentTime] = useState(
    dayjs(dayjs().tz(timezoneMode.timezoneMode)).tz(
      timezoneMode.timezoneMode,
      true,
    ),
  );

  const toKey = (status) => status.toLowerCase().replace(/\s+/g, "-");

  const groupByStatus = (data) => {
    // Step 1: create empty columns for all statuses
    const initialColumns = STATUS_LIST.reduce((cols, status) => {
      const key = toKey(status);
      cols[key] = { id: key, title: status, calls: [] };
      return cols;
    }, {});

    // Step 2: fill calls into each column
    data.forEach((item) => {
      const key = toKey(item.status);
      if (!initialColumns[key]) return;

      initialColumns[key].calls.push(item);
    });

    return { columns: initialColumns };
  };

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(
        dayjs(dayjs().tz(timezoneMode.timezoneMode)).tz(
          timezoneMode.timezoneMode,
          true,
        ),
      );
    }, 60000);

    return () => clearInterval(timer);
  }, [timezoneMode]);

  useEffect(() => {
    if (searchedData && Array.isArray(searchedData)) {
      const grouped = groupByStatus(searchedData);
      setGroupedData(grouped);
    }
  }, [searchedData]);

  const getFormattedDate = (startDate, endDate) => {
    const start = dayjs(startDate).tz(timezoneMode.timezoneMode);
    const end = dayjs(endDate).tz(timezoneMode.timezoneMode);

    // Get the day of the week, month, and date for the start date
    const formattedDate = start.format("YY-MM-DD");

    // Get the time in 12-hour format with AM/PM
    const startTime = start.format("hh:mma");
    const endTime = end.format("hh:mma");

    return `${formattedDate} ⋅ ${startTime}-${endTime}`;
  };

  const handleToggleExpand = (id) => {
    setExpandedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const getColorFromStatus = (status) => {
    const found = allStatus.find((item) => item.status === status);
    return found ? found.color : null;
  };

  const formattedText = (text) => {
    return text.split("\n").map((line, index) => (
      <span key={index}>
        {line.split(" ").map((text) => (
          <span>{text} </span>
        ))}
        <br />
      </span>
    ));
  };

  return (
    <div className="callHorizonViewSection">
      {Object.values(groupedData.columns).map((column) => (
        <div
          key={column.id}
          className={`call-horizontal-column ${
            !themeMode.themeMode
              ? "horizontal-light-mode"
              : "horizontal-dark-mode"
          }`}
        >
          <h3 className="call-horizontal-title">
            {column.title} ({column.calls.length})
          </h3>

          <div className="call-horizontal-list">
            {column.calls.map((interview) => {
              const interviewStart = dayjs(
                dayjs(interview.start_date).tz(timezoneMode.timezoneMode),
              ).tz(timezoneMode.timezoneMode, true);
              const isUpcoming = interviewStart.isAfter(currentTime);

              return (
                <div
                  key={interview.id}
                  className={`call-horizontal-card ${
                    !themeMode.themeMode
                      ? "horizontal-card-light-mode"
                      : "horizontal-card-dark-mode"
                  } ${isUpcoming ? "highlight-upcoming" : ""}`}
                  style={{ "--pulse-color": interview.caller_color }}
                >
                  <div className="call-horizontal-card-item">
                    <div
                      className="card-bar"
                      style={{ backgroundColor: interview.caller_color }}
                    ></div>
                    <div
                      className="card-item"
                      onClick={() => handleClickEdit(interview)}
                    >
                      <Tooltip placement="top" title={interview.company_name}>
                        <div className="card-item-company">
                          {interview.company_name}
                        </div>
                      </Tooltip>
                      <div className="card-item-time">
                        {getFormattedDate(
                          interview.start_date,
                          interview.end_date,
                        )}
                      </div>
                      <div className="card-item-action">
                        <div className="card-item-action-btns">
                          <Tooltip placement="top" title="Job Description">
                            {!isEmpty(interview.company_name) ? (
                              <Button
                                type="text"
                                size="small"
                                icon={<FileTextOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadJD(
                                    interview.job_desc,
                                    interview.company_name,
                                  );
                                }}
                              />
                            ) : (
                              <Button
                                disabled
                                size="small"
                                color="danger"
                                variant="dashed"
                                icon={<StopOutlined />}
                              />
                            )}
                          </Tooltip>
                          <Tooltip placement="top" title="PDF Resume">
                            {!isEmpty(interview.pdf_url) ? (
                              <Button
                                type="text"
                                size="small"
                                icon={<FilePdfOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadPDF(
                                    interview.pdf_url,
                                    interview.pdf_filename,
                                  );
                                }}
                              />
                            ) : (
                              <Button
                                disabled
                                size="small"
                                color="danger"
                                variant="dashed"
                                icon={<StopOutlined />}
                              />
                            )}
                          </Tooltip>
                          <Tooltip placement="top" title="Docx Resume">
                            {!isEmpty(interview.docx_url) ? (
                              <Button
                                type="text"
                                size="small"
                                icon={<FileWordOutlined />}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDownloadResume(interview.docx_url);
                                }}
                              />
                            ) : (
                              <Button
                                disabled
                                size="small"
                                color="danger"
                                variant="dashed"
                                icon={<StopOutlined />}
                              />
                            )}
                          </Tooltip>
                          {canUseSpecialActions && (
                            <Tooltip placement="top" title="Copy Prompt">
                              {!isEmpty(interview.docx_url) ? (
                                <Button
                                  type="text"
                                  size="small"
                                  icon={<CopyOutlined />}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleCopyDetail(interview);
                                  }}
                                />
                              ) : (
                                <Button
                                  disabled
                                  size="small"
                                  color="danger"
                                  variant="dashed"
                                  icon={<StopOutlined />}
                                />
                              )}
                            </Tooltip>
                          )}
                        </div>
                        <Button
                          type="text"
                          size="small"
                          icon={
                            expandedIds.includes(interview.id) ? (
                              <MinusCircleOutlined />
                            ) : (
                              <PlusCircleOutlined />
                            )
                          }
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleExpand(interview.id);
                          }}
                        />
                      </div>
                    </div>
                    <div className="card-avatar">
                      <Tooltip placement="top" title={interview.caller}>
                        <img
                          src={interview.caller_avatar}
                          alt="avatar"
                          className="card-caller-avatar"
                        />
                      </Tooltip>
                      <Tooltip placement="top" title={interview.bidder}>
                        <img
                          src={interview.bidder_avatar}
                          alt="avatar"
                          className="card-bidder-avatar"
                        />
                      </Tooltip>
                    </div>
                  </div>

                  <AnimatePresence>
                    {expandedIds.includes(interview.id) &&
                      (isEmpty(interview.call_log) ? (
                        <></>
                      ) : (
                        <motion.div
                          className="card-extra-content"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          exit={{ opacity: 0 }}
                        >
                          {interview.call_log.map((item, index) => {
                            return (
                              <div className="card-extra-item">
                                <div className="card-extra-header">
                                  <Tag
                                    color={getColorFromStatus(
                                      item.status === 0
                                        ? "Waiting"
                                        : item.status === 1
                                          ? "Recruiter Phone"
                                          : item.status === 2
                                            ? "Recruiter Video"
                                            : item.status === 7
                                              ? "HR Call"
                                              : item.status === 3
                                                ? "Technical"
                                                : item.status === 4
                                                  ? "Behavioural"
                                                  : item.status === 5
                                                    ? "Final"
                                                    : "Failed",
                                    )}
                                  >
                                    {item.status === 0
                                      ? "Waiting"
                                      : item.status === 1
                                        ? "Recruiter Phone"
                                        : item.status === 2
                                          ? "Recruiter Video"
                                          : item.status === 7
                                            ? "HR Call"
                                            : item.status === 3
                                              ? "Technical"
                                              : item.status === 4
                                                ? "Behavioural"
                                                : item.status === 5
                                                  ? "Final"
                                                  : "Failed"}
                                  </Tag>
                                  <Button
                                    type="primary"
                                    size="small"
                                    icon={<EditOutlined />}
                                    onClick={() =>
                                      handleReviewModalOpen(item, interview.id)
                                    }
                                  />
                                </div>
                                <div className="card-extra-body">
                                  {isEmpty(item?.desc) ? (
                                    <></>
                                  ) : (
                                    <div className="card-extra-desc">
                                      {formattedText(item.desc)}
                                    </div>
                                  )}
                                  {isEmpty(item?.feeling) ? (
                                    <></>
                                  ) : (
                                    <Rate
                                      tooltips={rateValues}
                                      value={
                                        isEmpty(item?.feeling)
                                          ? 0
                                          : item.feeling
                                      }
                                      character={
                                        <StarFilled style={{ fontSize: 12 }} />
                                      }
                                      disabled
                                    />
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </motion.div>
                      ))}
                  </AnimatePresence>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default CallHorizonView;
